import React = require('react');
import { ToastProps, ToastType, ToastWidget } from '@tableau/widgets';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { AppState } from '~src/bootstrap/redux/state/AppState';

import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '../bootstrap/redux/selectors/SessionInfoSelectors';
import { InformationObject } from '../bootstrap/redux/state/InlineBannerInfoState';
import { getAppStore } from '../bootstrap/redux/store/AppStore';
import { Messages } from '../codegen/Localize';
import { openReactConfirmationDialog } from '../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../commonInfra/L10n/L10n';
import { WindowLocationService } from '../commonInfra/services/WindowLocationService';
import { ILicenseConsumptionInfo } from '../license/serverApi/types/ILicenseConsumptionInfo';
import { fetchCapacityInfoForInfoBar, fetchSiteScopedCapacityInfoForInfoBar, hasSiteLicensingConsumptionInfo } from '../userManagement/services/licenses/LicensesService';
import { sendSiteExpirationTelemetry, SiteExpirationActions } from './SiteExpirationTelemetry';

const webstoreUrl = '{baseUrl}/subscribe-now?site_id={siteUUID}&source=tol&tableau_creator={creator}&tableau_explorer={explorer}&tableau_viewer={viewer}';

export interface NotificationState {
  siteUuid: string;
  isSiteExpiring: boolean;
  siteTypeName: string;
  isInlineBannerEnabled: boolean;
  subscribeNowEnabled: boolean;
  siteExpiringDays: number;
  webstoreUrl: string;
  siteContactSaleUrl: string;
  siteRenewUrl: string;
  siteRoleId: number;
}

export interface SiteExpirationParams  {
  site_uuid: string;
  creators: number;
  explorers: number;
  viewers: number;
}

export type RedirectParams = {
  siteExpirationActions: SiteExpirationActions;
  redirectUrl: string;
  siteExpirationParams: SiteExpirationParams;
}

export type isClickedParams = {
  isClicked: boolean;
}


export class SiteExpirationNotification {
  private notificationsShown: Map<string, boolean>;
  // Need to store the InformationObject for each site so that when the banner still display correct info
  // when switching back to a site that has notificationsShown === true, 
  private infos: Map<string, InformationObject>;
  private toastId: string; // Make sure that there is only one toast displayed for trial site notification when changing site

  constructor() {
    this.notificationsShown = new Map<string, boolean>();
    this.infos = new Map<string, InformationObject>();
  }

  public showNotificationsIfEnabled(): InformationObject {
    const state: NotificationState = this.builNotificationState(getAppStore().getState());
    const isSiteExpiring: boolean = state.isSiteExpiring;
    const siteUuid: string = state.siteUuid;
    const notificationShown: boolean = this.notificationsShown.get(siteUuid);
    const isInlineBannerEnabled: boolean = state.isInlineBannerEnabled;

    // Check two things: 
    // 1) If the notification is in display, don't do anything and just return it info object;
    // 2) Or, if the site doesn't expire and it is not an admin trial, don't do anything.
    if (!!notificationShown || !isSiteExpiring && !this.isAdminTrial(state)) {
      // If a previous trial site is converted to a purchased site, we remove it from the notificationsShown hashmap
      if (!!notificationShown && !isSiteExpiring && !this.isAdminTrial(state)) {
        this.notificationsShown.delete(siteUuid);
        this.infos.delete(siteUuid);
      }
      return this.infos.has(siteUuid) ? this.infos.get(siteUuid) : null;
    }

    // This InformationObject is only used for "Buy Now" inline warning
    let info: InformationObject;

    if (FeatureFlags.trialBannerDesignEnabled || isInlineBannerEnabled) {
      info = this.checkSiteExpiration(state);
      // info.display = false means that subscribeNowEnabled is false or a non-trial site, thus fall back to the toast design
      if (!info.display && notificationShown === undefined) {
        this.showToastDesign(state);
      }
    } else {
      this.showToastDesign(state);
    }

    this.notificationsShown.set(siteUuid, true);
    this.infos.set(siteUuid, info);

    return info;
  }

  // Check whether the site is opened by an admin
  private isAdminTrial(state: NotificationState): boolean {
    const siteTypeName: string = state.siteTypeName;
    const siteRoleId: number = state.siteRoleId;
    // Admin Roles are defined in SiteRole.java in monolith
    return (siteRoleId === 11 || siteRoleId === 0) && (siteTypeName === 'Trial');
  }

  // Redirect trial user to subscribenow dialogue pop up
  private processRedirect(siteExpirationParams: SiteExpirationParams) {
    const state = getAppStore().getState();
    // Only enter processRedirect() when "SubscribeNow" is enabled, so no need to check it here
    // directly redirect trial users to Webstore
    const baseUrl = SessionServerSelectors.webstoreUrl(state);
    const redirectUrl = webstoreUrl.replace('{baseUrl}', baseUrl)
                                  .replace('{siteUUID}', siteExpirationParams.site_uuid)
                                  .replace('{creator}', siteExpirationParams.creators.toString())
                                  .replace('{explorer}', siteExpirationParams.explorers.toString())
                                  .replace('{viewer}', siteExpirationParams.viewers.toString());
    SiteExpirationNotification.openSubscribeNowDialog({ siteExpirationActions: SiteExpirationActions.SubscribeNowClick1, redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams });
  }

  private showNotification(state: NotificationState, licenseInfo: ILicenseConsumptionInfo): void {
    const siteTypeName: string = state.siteTypeName;
    const siteExpiringDays = state.siteExpiringDays;

    let message: string;
    if (siteExpiringDays > 0) {
      if (siteTypeName === 'Trial') {
        message = Messages.siteDaysLeftInTrial({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
      } else {
        message = Messages.siteWillExpireInDays({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
      }
    }
    else {
      message = Messages.siteIsExpired();
    }
    let siteExpirationParams = {
      site_uuid: state.siteUuid,
      creators: 0,
      explorers: 0,
      viewers: 0
    };

    if (licenseInfo) {
      siteExpirationParams.creators = licenseInfo.usedCreatorUsers;
      siteExpirationParams.explorers = licenseInfo.usedExplorerUsers;
      siteExpirationParams.viewers = licenseInfo.usedViewerUsers;
    }

    if (siteTypeName === 'Trial') {
      // If the LD feature flag "SubscribeNow" is enabled, redirect trial users to Webstore
      if (state.subscribeNowEnabled) {
        const baseUrl = state.webstoreUrl;
        const redirectUrl = webstoreUrl.replace('{baseUrl}', baseUrl)
                                       .replace('{siteUUID}', siteExpirationParams.site_uuid)
                                       .replace('{creator}', siteExpirationParams.creators.toString())
                                       .replace('{explorer}', siteExpirationParams.explorers.toString())
                                       .replace('{viewer}', siteExpirationParams.viewers.toString());
        sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowDisplay, siteExpirationParams);
        const toastProps: ToastProps = {
          toastType: ToastType.Info,
          message: message,
          testId: 'trial-site-expire-to-webstore',
          onAction: () => SiteExpirationNotification.openSubscribeNowDialog({ siteExpirationActions: SiteExpirationActions.SubscribeNowClick, redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
          actionMessage: Messages.siteBuyNowButton(),
          onCloseRequest: () => this.toastId = undefined
        };

        // If a toast already exists, only update that toast instead of creating a new one
        if (this.toastId !== undefined) {
          ToastWidget.update(this.toastId, toastProps);
        } else {
          this.toastId = ToastWidget.show(toastProps);
        }
      }
      // If the LD feature flag "SubscribeNow" is not enabled, redirect trial users to Contact Tableau Page
      else {
        const redirectUrl = state.siteContactSaleUrl;
        const toastProps: ToastProps = {
          toastType: ToastType.Warning,
          message: message,
          testId: 'trial-site-expire-to-contact-us',
          onAction: () => this.redirectToContactSalePage({ siteExpirationActions: SiteExpirationActions.ContactSaleClick, redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
          actionMessage: Messages.siteContactSaleButton(),
          onCloseRequest: () => this.toastId = undefined
        };

        sendSiteExpirationTelemetry(SiteExpirationActions.ContactSaleDisplay, siteExpirationParams);

        if (this.toastId !== undefined) {
          ToastWidget.update(this.toastId, toastProps);
        } else {
          this.toastId = ToastWidget.show(toastProps);
        }
      }
    }
    // Any non-trial site is redirected to Renew Page
    else {
      const redirectUrl = state.siteRenewUrl;
      const toastProps: ToastProps = {
        toastType: ToastType.Warning,
        message: message,
        testId: 'non-trial-site-expire-to-renew',
        onAction:  () => this.redirectToRenewPage({ siteExpirationActions: SiteExpirationActions.RenewClick, redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
        actionMessage: Messages.siteRenewButton(),
        onCloseRequest: () => this.toastId = undefined
      };

      sendSiteExpirationTelemetry(SiteExpirationActions.RenewDisplay, siteExpirationParams);
      if (this.toastId !== undefined) {
        ToastWidget.update(this.toastId, toastProps);
      } else {
        this.toastId = ToastWidget.show(toastProps);
      }
    }
  }

  // Display original toast design
  private showToastDesign(state: NotificationState) {
    // Fetch the capacity information from License Service
    hasSiteLicensingConsumptionInfo().then((hasInfo: boolean) => {
      if (hasInfo) {
        fetchSiteScopedCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
          this.showNotification(state, licenseInfo);
        });
      }
      else {
        fetchCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
          this.showNotification(state, licenseInfo);
        });
      }
    });
  }

  // Redirect to Site Renew Page
  private redirectToRenewPage(params: RedirectParams) {
    sendSiteExpirationTelemetry(SiteExpirationActions.RenewClick, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
  }

  // Redirect to ContactSale Page
  private redirectToContactSalePage(params: RedirectParams) {
    sendSiteExpirationTelemetry(SiteExpirationActions.ContactSaleClick, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
  }

  // Open the SubscribeNow confirmation dialog
  // Set to static for processRedirect() to work
  private static openSubscribeNowDialog(params: RedirectParams) {
    sendSiteExpirationTelemetry(params.siteExpirationActions, params.siteExpirationParams);

    const initialModalInstance = openReactConfirmationDialog<RedirectParams, {}>({
      title: Messages.subscribeNow(),
      message: Messages.buyLicensesLeavingMessage(),
      actionName: Messages.buyLicensesWebstore(),
      action: (params: RedirectParams) => this.redirectToSubscribeNowPage(params),
      paramsForAction: (params),
      isActionDelete: false,
      dialogWidth: TabDialogWidth.width500px
    });
    return initialModalInstance.result;
  }

  // Redirect to SubscribeNow page (Webstore)
  // Set to static for processRedirect() to work
  private static redirectToSubscribeNowPage(params: RedirectParams): Promise<any> {
    sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowSubmit, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
    return;
  }

  private buildSiteExpirationParams(licenseInfo: ILicenseConsumptionInfo): SiteExpirationParams {
    const state = getAppStore().getState();
    let siteExpirationParams = {
      site_uuid: SessionSiteSelectors.siteUuid(state),
      creators: 0,
      explorers: 0,
      viewers: 0
    };
    if (licenseInfo) {
      siteExpirationParams.creators = licenseInfo.usedCreatorUsers;
      siteExpirationParams.explorers = licenseInfo.usedExplorerUsers;
      siteExpirationParams.viewers = licenseInfo.usedViewerUsers;
    }
    return siteExpirationParams;
  }

  // Check if the site is a trial site and record site information to info for inline banner display 
  private checkSiteExpiration(state: NotificationState): InformationObject {
    const siteTypeName = state.siteTypeName;
    const siteExpiringDays = state.siteExpiringDays;
    const subscribeNowEnabled = state.subscribeNowEnabled;

    let info: InformationObject = {
      message: '',
      display: false,
      action: null,
      actionMessage: '',
      redirectUrl: ''
    };

    if (siteExpiringDays > 0) {
      if (siteTypeName === 'Trial') {
        info.message = siteExpiringDays > 1 ? Messages.siteDaysLeftInTrial({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)}): Messages.siteDayLeftInTrial({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
      } else {
        info.message = Messages.siteWillExpireInDays({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
        return info;
      }
    }
    else {
      info.message = Messages.siteIsExpired();
    }
    info.actionMessage = Messages.siteBuyNowButton();
    info.display = subscribeNowEnabled && this.isAdminTrial(state);

    // Build the redirect link for inline banner's buy now button
    if (info.display) {
      // Fetch the capacity information from License Service
      hasSiteLicensingConsumptionInfo().then((hasInfo: boolean) => {
        let siteExpirationParams: SiteExpirationParams;
        if (hasInfo) {
          fetchSiteScopedCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
            siteExpirationParams = this.buildSiteExpirationParams(licenseInfo);
            sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowDisplay1, siteExpirationParams);
            info.action = () => this.processRedirect(siteExpirationParams);
          });
        }
        else {
          fetchCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
            siteExpirationParams = this.buildSiteExpirationParams(licenseInfo);
            sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowDisplay1, siteExpirationParams);
            info.action = () => this.processRedirect(siteExpirationParams);
          });
        }
      });
    }

    return info;
  }

  private builNotificationState(state: AppState): NotificationState {
    let notificationState = {
      siteUuid: SessionSiteSelectors.siteUuid(state),
      isSiteExpiring: SessionSiteSelectors.isSiteExpiring(state),
      siteTypeName: SessionSiteSelectors.siteTypeName(state),
      isInlineBannerEnabled: SessionSiteSelectors.isInlineBannerEnabled(state),
      subscribeNowEnabled: SessionSiteSelectors.subscribeNowEnabled(state),
      siteExpiringDays: SessionSiteSelectors.siteExpiringDays(state),
      webstoreUrl: SessionServerSelectors.webstoreUrl(state),
      siteContactSaleUrl: SessionServerSelectors.siteContactSaleUrl(state),
      siteRenewUrl: SessionServerSelectors.siteRenewUrl(state),
      siteRoleId: SessionUserSelectors.siteRoleId(state)
    };
    return notificationState;
  }

}

export const siteExpirationNotification = new SiteExpirationNotification();

